// src/pages/ReservationDetailsPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { API } from '../api/config';
import useReservationDetails from '../hooks/useReservationDetails';
import { CheckCircle2, Clock, Copy, Info, User, CalendarDays, AlertTriangle, ArrowLeft, ShieldCheck, Repeat, XCircle, UserCheck } from 'lucide-react';

import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Detalhes da Reserva`;

const formatTimeLeft = (ms) => {
    if (ms <= 0) return "00:00:00";
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const formatPhoneForUI = (value) => {
    if (!value) return "";
    let cleanValue = value.replace(/\D/g, "");
    
    if (cleanValue.startsWith("55") && cleanValue.length >= 12) {
        cleanValue = cleanValue.substring(2);
    }

    let formatted = cleanValue;
    if (cleanValue.length > 0) {
        formatted = `(${cleanValue.substring(0, 2)}`;
        if (cleanValue.length > 2) {
            formatted += `)${cleanValue.substring(2, 7)}`;
            if (cleanValue.length > 7) {
                formatted += `-${cleanValue.substring(7, 11)}`;
            }
        }
    }
    return formatted;
};

function ReservationDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading } = useContext(AuthContext);

    const { reservation, loadingReservation, errorReservation } = useReservationDetails(id);

    const [timeLeftString, setTimeLeftString] = useState("00:00:00");
    const [isExpired, setIsExpired] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);

    useEffect(() => {
        if (!loading && !user) {
            navigate("/login");
        }
        window.scrollTo(0, 0); 
    }, [user, loading, navigate]);

    useEffect(() => {
        if (!reservation) return;
        
        const rawStatus = (reservation.status || "").toUpperCase();
        if (rawStatus === 'PAID' || rawStatus === 'CANCELED' || rawStatus.includes('CHECKIN')) return;
        
        if (!reservation.createdAt) return; 

        const updateTimer = () => {
            const createdTime = new Date(reservation.createdAt).getTime();
            const expirationTime = createdTime + (2 * 60 * 60 * 1000); 
            const now = Date.now();
            const diff = expirationTime - now;

            if (diff <= 0) {
                setTimeLeftString("00:00:00");
                setIsExpired(true);
            } else {
                setTimeLeftString(formatTimeLeft(diff));
            }
        };

        updateTimer();
        const intervalId = setInterval(updateTimer, 1000);
        return () => clearInterval(intervalId);
    }, [reservation]);

    if (loading || loadingReservation) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE]">
                <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#528a89] rounded-full animate-spin mb-4"></div>
                <p className="text-[#528a89] font-serif text-lg animate-pulse">Desvendando os pergaminhos...</p>
            </div>
        );
    }

    if (errorReservation) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE] p-4 text-center">
                <AlertTriangle size={56} className="text-[#971253] mb-4 opacity-80" />
                <h2 className="font-serif text-3xl font-bold text-stone-800 mb-2">{errorReservation}</h2>
                <button 
                    onClick={() => navigate('/minhas-reservas')} 
                    className="mt-6 px-8 py-3 bg-[#528a89] text-[#FFF5EE] rounded-xl font-bold hover:bg-[#3a6362] transition-colors shadow-lg hover:-translate-y-1"
                >
                    Voltar para Minhas Reservas
                </button>
            </div>
        );
    }

    if (!reservation) return null; 

    // --- Mapeamento Exato dos 4 Status ---
    const rawStatus = (reservation.status || 'WAITING_PAYMENT').toUpperCase();
    const isCanceled = rawStatus === 'CANCELED' || rawStatus === 'CANCELADA';
    const isCheckin = rawStatus.includes('CHECKIN') || rawStatus.includes('CHECK-IN');
    const isPaid = rawStatus === 'PAID' || rawStatus === 'CONFIRMADA';
    const isPending = !isCanceled && !isCheckin && !isPaid; 
    
    const isSplit = reservation.paymentType === 'SPLIT';
    const isRPG = (reservation.type || "").toUpperCase() === 'RPG';

    const amountPaid = reservation?.participants?.reduce((acc, p) => p.status === 'PAID' ? acc + (p.amount || 0) : acc, 0) || 0;
    
    const remainingAmount = isPaid || isCanceled || isCheckin ? 0 : Math.max(0, (reservation?.totalPrice || 0) - amountPaid);
    const totalLabel = isCanceled ? "Valor Total" : (isPaid || isCheckin ? "Valor Total Pago" : (isSplit && amountPaid > 0 ? "Falta Pagar" : "Valor Total"));
    const displayAmount = isPaid || isCanceled || isCheckin ? (reservation?.totalPrice || 0) : remainingAmount;

    // ========================================================
    // CÁLCULO DA TRAVA DE CANCELAMENTO (Mínimo 72 horas)
    // ========================================================
    let canCancel = false;
    let cancelText = "Cancelar Reserva";

    if (reservation.rawDate && reservation.time) {
        // Divide e converte YYYY-MM-DD e HH:mm (Mês -1 pois JS Date o mês começa em 0)
        const [year, month, day] = reservation.rawDate.split('-').map(Number);
        const [hour, minute] = reservation.time.split(':').map(Number);
        
        const resDate = new Date(year, month - 1, day, hour, minute);
        const now = new Date();
        const hoursDiff = (resDate.getTime() - now.getTime()) / (1000 * 60 * 60);

        if (hoursDiff > 72) {
            canCancel = true;
        } else if (hoursDiff > 0) {
            cancelText = "Prazo esgotado (< 72h)";
        } else {
            cancelText = "Reserva passada";
        }
    }
    // ========================================================

    const handleRepeatReservation = () => {
        const typeMap = {
            "BOARD GAME": "boardgame",
            "RPG": "rpg",
            "DELI PARTY": "deliparty",
            "KIDS": "kids"
        };
        const rType = (reservation.type || "").toUpperCase();
        const mappedType = typeMap[rType] || "boardgame";

        let prefillRpgSystem = null;
        if (isRPG && reservation.obs && reservation.obs.includes("Sistema:")) {
            prefillRpgSystem = reservation.obs.replace("Sistema:", "").trim();
        }

        const cleanParticipants = (reservation.participants || []).map(p => ({
            name: p.name,
            phone: formatPhoneForUI(p.phone), 
        }));

        navigate('/reserva', {
            state: {
                prefillData: {
                    reservationType: mappedType,
                    numPeople: reservation.participants?.length || 2,
                    selectedRpgSystem: prefillRpgSystem,
                    participants: cleanParticipants
                }
            }
        });
    };

    const handleCancelReservation = async () => {
        const confirmMessage = isPaid 
            ? "Tem certeza que deseja cancelar esta reserva confirmada? Nossa equipe será notificada para realizar os devidos reembolsos."
            : "Tem certeza que deseja cancelar esta reserva?";

        if (!window.confirm(confirmMessage)) return;

        setIsCanceling(true);

        try {
            const token = user?.token || user?.googleToken;
            
            const response = await fetch(`${API.RESERVAS}/cancelarMinhaReserva`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    googleToken: token,
                    reservaId: id
                })
            });

            if (!response.ok) {
                const errData = await response.json().catch(() => ({}));
                throw new Error(errData.message || "Falha ao cancelar a reserva no servidor.");
            }

            alert("Reserva cancelada com sucesso!");
            window.location.reload(); 

        } catch (error) {
            console.error("Erro ao cancelar reserva:", error);
            alert("Erro ao tentar cancelar a reserva: " + error.message);
        } finally {
            setIsCanceling(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FFF5EE] pt-24 pb-32 relative overflow-hidden font-sans text-stone-800">
             <title>{TITLE}</title>
             <link rel="icon" href={ICON} />

             <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

             <div className="max-w-3xl mx-auto px-4 md:px-6 relative z-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
                
                <button 
                    onClick={() => navigate('/minhas-reservas')}
                    className="flex items-center gap-2 text-stone-400 hover:text-[#971253] font-bold uppercase tracking-widest text-xs mb-8 transition-colors group"
                >
                    <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" /> 
                    Voltar ao Histórico
                </button>

                <div className="space-y-6">
                    
                    {/* 1️⃣ Header Card */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-8 md:p-10">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-6 border-b border-[#dbcdac]/30 pb-6 mb-6">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-widest text-[#dbcdac] mb-2 block">
                                    Reserva #{reservation.id}
                                </span>
                                <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#971253]">
                                    {reservation.type}
                                </h1>
                            </div>
                            
                            {/* BADGE DE STATUS DINÂMICO DOS 4 ESTADOS */}
                            <div className={`px-4 py-2 rounded-xl text-sm font-bold uppercase tracking-wider flex items-center gap-2 shadow-sm ${
                                isCheckin ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                isPaid ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 
                                isCanceled ? 'bg-stone-100 text-stone-500 border border-stone-200' : 
                                'bg-[#971253]/10 text-[#971253] border border-[#971253]/20'
                            }`}>
                                {isCheckin ? <><UserCheck size={18}/> No Local</> :
                                 isPaid ? <><CheckCircle2 size={18}/> Confirmada</> : 
                                 isCanceled ? <><XCircle size={18}/> Cancelada</> : 
                                 <><Clock size={18}/> Aguardando</>}
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row sm:items-center gap-6 text-stone-600">
                            <div className={`flex items-center gap-2 font-medium ${isCanceled && 'opacity-60'}`}>
                                <CalendarDays size={20} className={isCanceled ? 'text-stone-400' : 'text-[#528a89]'} /> 
                                <span className="text-lg">{reservation.date}</span>
                            </div>
                            <div className={`flex items-center gap-2 font-medium ${isCanceled && 'opacity-60'}`}>
                                <Clock size={20} className={isCanceled ? 'text-stone-400' : 'text-[#528a89]'} /> 
                                <span className="text-lg">{reservation.time}</span>
                            </div>
                        </div>
                    </div>

                    {/* 2️⃣ Warning Blocks: Timer ou Aviso de Cancelamento */}
                    {isCanceled && (
                        <div className="bg-stone-100 border border-stone-200 rounded-[2rem] p-8 flex flex-col items-center justify-center text-center space-y-3 shadow-inner">
                            <h3 className="text-stone-500 font-bold flex items-center gap-2 text-xl">
                                <XCircle size={24} /> 
                                Reserva Cancelada
                            </h3>
                            <p className="text-stone-500/80 font-medium">Esta reserva foi cancelada. Nenhum pagamento será cobrado ou já foi estornado.</p>
                        </div>
                    )}

                    {/* Só mostra o Timer se estiver pendente */}
                    {isPending && (
                        <div className="bg-[#971253]/5 border border-[#971253]/20 rounded-[2rem] p-8 flex flex-col items-center justify-center text-center space-y-3 shadow-inner">
                            <h3 className="text-[#971253] font-bold flex items-center gap-2 text-lg">
                                <Clock size={22} /> 
                                {isExpired ? "O tempo esgotou!" : "Garanta sua mesa antes que o tempo acabe"}
                            </h3>
                            {!isExpired && reservation.createdAt && (
                                <div className="font-serif text-5xl md:text-6xl font-bold text-[#971253] tracking-wider drop-shadow-sm">
                                    {timeLeftString}
                                </div>
                            )}
                            {isExpired && (
                                <p className="text-[#971253]/80 font-medium max-w-md mx-auto">
                                    O limite para o pagamento expirou. Reembolsos automáticos (caso algum participante já tenha pago) serão processados por nossa equipe.
                                </p>
                            )}
                        </div>
                    )}

                    {/* 3️⃣ Info / Benefits Card */}
                    {reservation?.benefits?.length > 0 && (
                        <div className={`bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-8 md:p-10 ${isCanceled && 'opacity-60 grayscale'}`}>
                            <h3 className="font-serif text-2xl text-[#528a89] flex items-center gap-2 mb-6">
                                <Info size={24} className="text-[#dbcdac]" /> Detalhes da Experiência
                            </h3>
                            <div className="bg-[#FFF5EE]/50 border border-[#dbcdac]/30 rounded-2xl p-6">
                                <ul className="space-y-4">
                                    {reservation.benefits.map((benefit, index) => (
                                        <li key={index} className="flex items-start gap-3 text-stone-600 font-medium">
                                            <CheckCircle2 size={20} className="text-[#528a89] shrink-0 mt-0.5" />
                                            <span className="leading-relaxed">{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}

                    {/* 4️⃣ Participants Card */}
                    {!isRPG && reservation?.participants?.length > 0 && (
                        <div className={`bg-white/80 backdrop-blur-sm rounded-[2rem] border border-[#dbcdac]/40 shadow-sm p-8 md:p-10 ${isCanceled && 'opacity-60 grayscale'}`}>
                            <h3 className="font-serif text-2xl text-[#528a89] flex items-center gap-2 mb-6">
                                <User size={24} className="text-[#dbcdac]" /> Participantes ({reservation.participants.length})
                            </h3>
                            <div className="space-y-4">
                                {reservation.participants.map((p, index) => (
                                    <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-[#FFF5EE]/50 border border-[#dbcdac]/20 rounded-2xl p-5 gap-4 hover:border-[#dbcdac]/60 transition-colors">
                                        <span className="font-bold text-stone-700 text-lg flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-[#528a89]/10 text-[#528a89] flex items-center justify-center text-sm">
                                                {index + 1}
                                            </div>
                                            {p.name || `Participante ${index + 1}`}
                                        </span>
                                        
                                        {isSplit && (
                                            <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-0 border-[#dbcdac]/20 pt-4 sm:pt-0">
                                                {p.status === 'PAID' ? (
                                                    <span className="text-emerald-600 font-bold flex items-center gap-1.5 bg-emerald-50 px-3 py-1 rounded-lg">
                                                        <CheckCircle2 size={18} /> Pago
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span className="font-serif font-bold text-[#971253] text-xl">
                                                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(p.amount || 0)}
                                                        </span>
                                                        {isPending && !isExpired && p.link && (
                                                            <div className="flex items-center gap-2">
                                                                <button 
                                                                    onClick={() => window.open(p.link, '_blank')}
                                                                    className="bg-[#528a89] hover:bg-[#3a6362] text-[#FFF5EE] px-5 py-2 rounded-xl text-sm font-bold tracking-wide transition-colors shadow-md"
                                                                >
                                                                    Pagar
                                                                </button>
                                                                <button 
                                                                    className="p-2 text-[#528a89] hover:text-[#FFF5EE] bg-[#528a89]/10 hover:bg-[#528a89] rounded-xl transition-colors"
                                                                    title="Copiar Link"
                                                                    onClick={() => navigator.clipboard.writeText(p.link)}
                                                                >
                                                                    <Copy size={18} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* 5️⃣ Payment Summary & Footer Card */}
                    <div className={`bg-gradient-to-br p-8 md:p-12 rounded-[2rem] shadow-2xl text-white relative overflow-hidden mt-8 ${
                        isCanceled ? 'from-stone-600 to-stone-800' : 'from-[#528a89] to-[#3a6362]'
                    }`}>
                        <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
                        
                        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 md:gap-12">
                            
                            <div className="w-full md:w-1/2 flex flex-col items-center md:items-start text-center md:text-left">
                                <h3 className="font-serif text-3xl text-white/90 mb-2">Resumo Financeiro</h3>
                                <p className="text-white/70 font-medium mb-4 md:mb-6">
                                    Modo: <span className="text-white font-bold">{isSplit ? "Divisão de Conta" : "Pagamento Único"}</span>
                                </p>
                                
                                {isSplit && amountPaid > 0 && isPending && (
                                    <p className="text-[#dbcdac] font-bold bg-black/20 px-4 py-1.5 rounded-lg mb-4 text-sm border border-white/10">
                                        Já pago: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amountPaid)}
                                    </p>
                                )}

                                <div className="flex flex-col items-center md:items-start mt-2">
                                    <span className="text-sm font-bold uppercase tracking-widest text-white/70 mb-1">
                                        {totalLabel}
                                    </span>
                                    <span className={`font-serif text-5xl md:text-6xl font-bold tracking-tight drop-shadow-md ${isCanceled ? 'text-white/50 line-through' : 'text-white'}`}>
                                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(displayAmount)}
                                    </span>
                                </div>
                            </div>

                            <div className="w-full md:w-1/2 flex flex-col gap-4 mt-4 md:mt-0">
                                {isCanceled ? (
                                    <>
                                        <div className="w-full py-4 px-6 bg-black/20 border border-white/10 text-white/70 rounded-xl font-bold tracking-wider text-base md:text-lg flex items-center justify-center gap-2 backdrop-blur-sm text-center">
                                            <XCircle size={24} className="shrink-0"/> Reserva Cancelada
                                        </div>
                                        <button 
                                            onClick={handleRepeatReservation}
                                            className="w-full py-4 px-6 bg-white/10 text-white hover:bg-white/20 rounded-xl font-bold tracking-wider text-base md:text-lg transition-all shadow-md flex items-center justify-center gap-2 hover:-translate-y-0.5 group"
                                        >
                                            <Repeat size={20} className="shrink-0 group-hover:rotate-180 transition-transform duration-500" />
                                            Fazer Nova Reserva
                                        </button>
                                    </>
                                ) : isCheckin ? (
                                    <>
                                        <div className="w-full py-4 px-6 bg-white/10 border border-white/20 text-white rounded-xl font-bold tracking-wider text-base md:text-lg flex items-center justify-center gap-2 backdrop-blur-sm text-center">
                                            <UserCheck size={24} className="shrink-0"/> Cliente no Local
                                        </div>
                                        <button 
                                            onClick={handleRepeatReservation}
                                            className="w-full py-4 px-6 bg-[#dbcdac] text-[#3a6362] hover:bg-white rounded-xl font-bold tracking-wider text-base md:text-lg transition-all shadow-md flex items-center justify-center gap-2 hover:-translate-y-0.5 group"
                                        >
                                            <Repeat size={20} className="shrink-0 group-hover:rotate-180 transition-transform duration-500" />
                                            Nova Reserva
                                        </button>
                                    </>
                                ) : isPaid ? (
                                    <>
                                        <div className="w-full py-4 px-6 bg-white/10 border border-white/20 text-white rounded-xl font-bold tracking-wider text-base md:text-lg flex items-center justify-center gap-2 backdrop-blur-sm text-center">
                                            <CheckCircle2 size={24} className="shrink-0"/> Pagamento Confirmado
                                        </div>
                                        <button 
                                            onClick={handleRepeatReservation}
                                            className="w-full py-4 px-6 bg-[#dbcdac] text-[#3a6362] hover:bg-white rounded-xl font-bold tracking-wider text-base md:text-lg transition-all shadow-md flex items-center justify-center gap-2 hover:-translate-y-0.5 group"
                                        >
                                            <Repeat size={20} className="shrink-0 group-hover:rotate-180 transition-transform duration-500" />
                                            Segunda Rodada
                                        </button>

                                        <button 
                                            onClick={handleCancelReservation}
                                            disabled={isCanceling || !canCancel}
                                            title={!canCancel ? "Cancelamento apenas com 72h de antecedência." : ""}
                                            className={`w-full py-3 px-6 bg-transparent border border-white/30 text-white/90 rounded-xl font-medium tracking-wide transition-colors ${(isCanceling || !canCancel) ? 'opacity-50 cursor-not-allowed' : 'hover:text-white hover:bg-white/10'}`}
                                        >
                                            {isCanceling ? "Cancelando..." : cancelText}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button 
                                            onClick={() => window.open(reservation.participants[0]?.link, '_blank')}
                                            disabled={isExpired}
                                            className={`w-full py-4 px-4 sm:px-6 rounded-xl font-bold text-base md:text-lg transition-all shadow-lg flex items-center justify-center gap-2 ${isExpired ? 'bg-white/20 text-white/50 cursor-not-allowed' : 'bg-[#971253] hover:bg-[#7a0e42] text-[#FFF5EE] hover:-translate-y-1'}`}
                                        >
                                            <ShieldCheck size={22} className="shrink-0" />
                                            <span>{isSplit ? "Pagar Valor Restante" : "Realizar Pagamento"}</span>
                                        </button>
                                        
                                        <button 
                                            onClick={handleCancelReservation}
                                            disabled={isCanceling || isExpired || !canCancel}
                                            title={!canCancel ? "Cancelamento apenas com 72h de antecedência." : ""}
                                            className={`w-full py-3 px-6 bg-transparent border border-white/30 text-white/90 rounded-xl font-medium tracking-wide transition-colors ${(isCanceling || isExpired || !canCancel) ? 'opacity-50 cursor-not-allowed' : 'hover:text-white hover:bg-white/10'}`}
                                        >
                                            {isExpired ? "Cancelamento Automático" : (isCanceling ? "Cancelando..." : cancelText)}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}

export default ReservationDetailsPage;